import useGraphql, {
  TRANSPORT_ERROR_MESSAGE,
  NO_DATA_ERROR_MESSAGE,
} from "@/composables/useGraphql";

// useGraphql rejects with the server's own message whenever the server
// answered; these two are the only reasons it produces when it did not.
const isServerSilent = (reason: string) =>
  reason === TRANSPORT_ERROR_MESSAGE || reason === NO_DATA_ERROR_MESSAGE;
import * as cartQueries from "@/graphql/queries/cart";
import * as commonQueries from "@/graphql/queries/common";

import { defineStore, acceptHMRUpdate } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useOrderStore } from "@/stores/order";
import { useGlobalStore } from "@/stores/global";

import {
  CartInput,
  ProductInput,
  SubsOrderDataInput,
  SpecifyDeliveryDateInput,
} from "@/types/input/CartInput";
import { CustomersCustomerType } from "@/types/customers/CustomersCustomerType";
import { CustomersShippingAddressType } from "@/types/customers/CustomersShippingAddressType";
import {
  CustomersCardType,
  CustomersPayjpCardType,
  CustomersGmoPgCardType,
} from "@/types/customers/CustomersCardType";
import { PublicsOrdersPricesType } from "@/types/publics/orders/PricesType";
import { PublicsOrdersResultType } from "@/types/publics/orders/ResultType";
import { SharedGeneralsSelectOptionType } from "@/types/shared/generals/SharedGeneralsSelectOptionType";
import { SharedGeneralsSelectOptionsType } from "@/types/shared/generals/SharedGeneralsSelectOptionsType";

import { ShippingCarrierOptionType } from "@/types/shared/generals/ShippingCarrierOptionType";
import { UrlType, AvailableCouponType } from "@/types/UrlType";
import { OrderSettingType } from "@/types/OrderSettingType";
import { OfferType } from "@/types/publics/orders/OfferType";
import { VariantSelectedInput } from "@/types/input/OfferInput";
import { NORMAL_MODE, XSELL_MODE, UPSELL_MODE } from "@/helpers/constants";

import _ from "lodash";

const CART_SUBMITTED_ATTRIBUTES = [
  "productsData",
  "billingAddressData",
  "shippingAddressData",
  "customerData",
  "paymentData",
  "urlData",
  "shippingCarrierId",
  "specifyDeliveryDate",
  "subsOrderData",
  "couponCode",
];
const CART_UNSUBMITTED_ATTRIBUTES = [
  "urlData.productUpsellId",
  "paymentData.isAddNewCard",
  "paymentData.card",
];
const CUSTOMER_NONLOGIN_ATTRIBUTES = [
  "customerData.emailConfirmation",
  "customerData.email",
  "customerData.password",
  "customerData.passwordConfirmation",
];

type State = {
  urlInfo: UrlType;
  availableCoupons: AvailableCouponType[];
  cart: CartInput;
  orderInfo: PublicsOrdersPricesType | null;
  orderSummary: PublicsOrdersPricesType | null;
  shippingAddresses: CustomersShippingAddressType[];
  prefectures: SharedGeneralsSelectOptionType[];
  sexes: SharedGeneralsSelectOptionType[];
  dateOfWeekJp: SharedGeneralsSelectOptionType[];
  shippingCarrierOptions: any[];
  shippingCarrierOptionsCvUpsell: any;
  shippingCarrierOptionsXsell: any[];
  shippingCarrierDefault: any[];
  payjpCards: CustomersPayjpCardType[];
  gmoPgCards: CustomersGmoPgCardType[];
  softbankCards: CustomersCardType[];
  paygentCards: CustomersGmoPgCardType[];
  offerData: any;
  preventiveProductsData: ProductInput[];
  deliveryTimesOptions: any;
  deliveryDateAtOptions: any[];
  preventiveSubsOrderData: SubsOrderDataInput;
  preventiveSpecifyDeliveryDate: SpecifyDeliveryDateInput;
  preventiveShippingCarrierId: number | null;
  cartProps: any;
  submitWithNewCard: any;
  currentMode: string;
  currentBaseUrl: string;
  lastPage: string;
  amazonBuyerData: any;
  checkoutSessionSignature: string | null;
  orderNumberCvConfirm: string;
};

export const useCartStore = defineStore({
  id: "cart",

  state: (): State => ({
    availableCoupons: [],
    urlInfo: {
      baseUrl: "",
      upsell: false,
      skipConfirm: false,
      skipCvConfirm: false,
      products: [],
      availablePaymentMethodOptions: [],
      availablePaymentMethods: [],
      productQuantityDisplay: false,
      deliveryCompanyDisplay: false,
      confirmEmailDisplay: false,
      passwordDisplay: false,
      confirmPasswordDisplay: false,
      optinDisplay: false,
      defaultOptinEnabled: false,
      generalVariantDisplay: false,
      ownVariantDisplay: false,
      caution02Display: false,
      cautionDisplay: false,
      couponDisplay: false,
      availableCouponListDisplay: false,
      id: 0,
      lineLoginFormDisplay: false,
      lineLoginThanksDisplay: false,
      sexRequiredEnabled: false,
      birthdayRequiredEnabled: false,
      sexDisplay: false,
      birthdayDisplay: false,
      normalProductAllowScheduleDeliveryDate: false,
      normalProductAllowScheduleDeliveryTime: false,
      normalProductShowScheduledDeliveryDates: false,
      normalProductAllowNilInDeliveryDate: false,
      subsProductAllowScheduleDeliveryDate: false,
      subsProductAllowScheduleDeliveryTime: false,
      subsProductShowScheduledDeliveryDates: false,
      subsProductAllowNilInDeliveryDate: false,
      subsProductAllowScheduleDeliveryCycleByDate: false,
      subsProductAllowScheduleDeliveryCycleByDayOfWeek: false,
      subsProductAllowScheduleDeliveryCycleByTerm: false,
      buyMultipleVariant: false,
      productsUrls: [],
      recurringOptions: {
        paymentScheduleOptions: [],
        dateOptions: [],
        termOptions: [],
      },
    },
    cart: {
      productsData: [
        {
          id: null,
          quantity: 1,
          isRecurring: false,
          variantData: [
            {
              variantId: null,
              quantity: null,
              valueOptions: [
                {
                  id: null,
                  value: null,
                },
              ],
            },
          ],
        },
      ],
      billingAddressData: {
        name01: "",
        name02: "",
        kana01: "",
        kana02: "",
        zip01: "",
        zip02: "",
        prefectureId: "",
        addr01: "",
        addr02: "",
        tel01: "",
        tel02: "",
        tel03: "",
      },
      shippingAddressData: {
        sameWithBillingAddress: true,
      },
      customerData: {
        email: "",
        emailConfirmation: "",
        password: "",
        passwordConfirmation: "",
        sexId: null,
        birthday: null,
        optin: false,
      },
      paymentData: {
        isAddNewCard: false,
        paymentMethodCode: "",
        cardToken: "",
        cardTokenKey: "",
        sourceId: "",
        card: {
          cardNumber: "",
          cardName: "",
          cardExpiry: "",
          cardCvc: "",
        },
      },
      urlData: {
        baseUrl: "",
        upsellBack: false,
        upsellNormal: false,
        productUpsellId: null,
      },
      shippingCarrierId: null,
      specifyDeliveryDate: {
        scheduledDeliveryTime: "",
        scheduledToBeDeliveredAt: null,
      },
      subsOrderData: {
        paymentSchedule: "",
        scheduledToBeDeliveredEveryXMonth: null,
        scheduledToBeDeliveredOnXthDay: null,
        scheduledToBeDeliveredEveryXDay: null,
        scheduledToBeDeliveredEveryXDayOfWeek: null,
        scheduledToBeDeliveredOnXthDayOfWeek: null,
        scheduledToBeDeliveredAt: "",
      },
      couponCode: "",
      termAccepted: false,
    },
    orderInfo: null,
    orderSummary: null,
    shippingAddresses: [],
    prefectures: [],
    sexes: [],
    dateOfWeekJp: [],
    payjpCards: [],
    gmoPgCards: [],
    softbankCards: [],
    paygentCards: [],
    offerData: {},
    shippingCarrierOptions: [],
    shippingCarrierOptionsCvUpsell: {},
    shippingCarrierOptionsXsell: [],
    shippingCarrierDefault: [],
    preventiveProductsData: [],
    deliveryTimesOptions: [],
    deliveryDateAtOptions: [],
    preventiveSubsOrderData: {
      paymentSchedule: "",
      scheduledToBeDeliveredEveryXMonth: null,
      scheduledToBeDeliveredOnXthDay: null,
      scheduledToBeDeliveredEveryXDay: null,
      scheduledToBeDeliveredEveryXDayOfWeek: null,
      scheduledToBeDeliveredOnXthDayOfWeek: null,
      scheduledToBeDeliveredAt: "",
    },
    preventiveSpecifyDeliveryDate: {
      scheduledDeliveryTime: "",
      scheduledToBeDeliveredAt: null,
    },
    preventiveShippingCarrierId: null,
    cartProps: {},
    submitWithNewCard: null,
    currentMode: NORMAL_MODE,
    currentBaseUrl: "",
    lastPage: "lp",
    amazonBuyerData: null,
    checkoutSessionSignature: "",
    orderNumberCvConfirm: "",
  }),

  actions: {
    // options: the thanks page calls this only to decide whether to show the
    // LINE button, and must be able to turn off the toast and the loading
    // overlay - both are raised inside useGraphql, so a .catch at the call
    // site cannot suppress them.
    // The default must repeat useGraphql's own, not be {}: a default parameter
    // applies only when the argument is undefined, so an empty object would
    // leave errorToasted undefined and silently suppress the error toast for
    // every caller that passes no options - including the LP purchase form.
    async fetchUrl(
      baseUrl: string,
      options: { errorToasted?: boolean; loading?: boolean } = { errorToasted: true },
    ) {
      const data = await useGraphql<UrlType>(
        cartQueries.url,
        {
          baseUrl,
          userAgent: window.navigator.userAgent,
        },
        options,
      );

      this.urlInfo = data;
      this.cart.urlData.baseUrl = this.urlInfo.baseUrl;
      if (!this.cart.paymentData.paymentMethodCode)
        this.setPaymentMethodCode(this.urlInfo.availablePaymentMethodOptions?.[0]?.value ?? "");
    },

    async deliveryTimes(shippingCarrierId: any) {
      const data = await useGraphql<ShippingCarrierOptionType>(cartQueries.deliveryTimes, {
        shippingCarrierId: shippingCarrierId,
      });
      this.deliveryTimesOptions = data;
    },

    async orderSetting(id: any) {
      const data = await useGraphql<OrderSettingType>(cartQueries.orderSetting, {
        prefectureId: id,
      });
      this.deliveryDateAtOptions = data.deliveryDateAtOptions;
    },

    async fetchCustomer() {
      const data = await useGraphql<CustomersCustomerType>(
        cartQueries.customer,
        {},
        { errorToasted: false },
      );
      _.assign(this.shippingAddresses, data.shippingAddresses);
      _.assign(this.cart.billingAddressData, data.billingAddress);
      this.cart.customerData.emailConfirmation = data.email;
      this.cart.customerData.email = data.email;
      this.cart.customerData.birthday = data.birthday;
      this.cart.customerData.sexId = data.sexId;
      this.cart.customerData.optin = data.optin;
    },

    async fetchSelectOptions() {
      const data = await useGraphql<SharedGeneralsSelectOptionsType>(commonQueries.selectOptions, {
        keys: ["sexes", "prefectures", "day_of_week_jp_options"],
      });
      this.prefectures = data.prefectures;
      this.sexes = data.sexes;
      this.dateOfWeekJp = data.dayOfWeekJpOptions;
    },

    async fetchShippingCarrierOptions(payload: any, mode = NORMAL_MODE) {
      const data = await useGraphql<ShippingCarrierOptionType[]>(
        commonQueries.fetchShippingCarrierOptions,
        payload,
      );

      mode == XSELL_MODE
        ? (this.shippingCarrierOptionsXsell = data)
        : (this.shippingCarrierOptions = data);
    },

    async fetchShippingCarrierDefault() {
      const data = await useGraphql<ShippingCarrierOptionType[]>(
        commonQueries.fetchShippingCarrierDefault,
      );
      this.shippingCarrierDefault = data;
    },

    async fetchAvailableCoupons(baseUrl: string, productData: any) {
      const data = await useGraphql<AvailableCouponType[]>(commonQueries.fetchAvailableCoupons, {
        input: { baseUrl, productData },
      });
      this.availableCoupons = data;
    },

    async ordersPaymentAmazonPayGetBuyerInfo(buyerToken: string) {
      const data = await useGraphql(commonQueries.ordersPaymentAmazonPayGetBuyerInfo, {
        buyerToken,
      });

      this.amazonBuyerData = data;
    },

    async orderPaymentAmazonPayGetCreateCheckoutSessionSignature(
      addressDetails: any,
      amount: string,
      isRecurring: boolean,
    ) {
      try {
        const data = await useGraphql(
          commonQueries.orderPaymentAmazonPayGetCreateCheckoutSessionSignature,
          { addressDetails, amount, isRecurring },
        );
        this.checkoutSessionSignature = String(data);
      } catch (e) {
        console.log(e);
      }
    },

    async orderPaymentPaygentThreeDSecure(input: any) {
      try {
        const data = await useGraphql(commonQueries.orderPaymentPaygentThreeDSecure, { input });
        return data;
      } catch (e) {
        console.log(e);
      }
    },

    async validateCart(upsellNormal = false) {
      if (upsellNormal) {
        this.cart.urlData.upsellNormal = true;
        this.cart.urlData.productUpsellId = this.cart.productsData[0].id;

        this.preventiveProductsData = this.cart.productsData;
        this.preventiveSubsOrderData = this.cart.subsOrderData;
        this.preventiveSpecifyDeliveryDate = this.cart.specifyDeliveryDate;
        this.preventiveShippingCarrierId = this.cart.shippingCarrierId;

        this.cart.subsOrderData = useOrderStore().$state.upSellSubsOrderData;
        this.cart.productsData = [useOrderStore().$state.upSellVariantData];
        this.cart.specifyDeliveryDate = useOrderStore().$state.upSellSpecifyDeliveryDate;
        this.cart.shippingCarrierId = useOrderStore().$state.upSellShippingCarrierId;
      }

      const data = await useGraphql<PublicsOrdersPricesType>(cartQueries.validateCart, {
        input: this.cartSubmitVariables,
      }).catch(() => {});

      if (!data) {
        this.cart.urlData.upsellNormal = false;

        this.cart.productsData = this.preventiveProductsData;
        this.cart.subsOrderData = this.preventiveSubsOrderData;
        this.cart.specifyDeliveryDate = this.preventiveSpecifyDeliveryDate;
        this.cart.shippingCarrierId = this.preventiveShippingCarrierId;

        this.cart.urlData.productUpsellId = null;

        if (upsellNormal) useGlobalStore().$state.keepBaseError = true;
        return false;
      }

      useGlobalStore().resetValidationBaseErrors();

      this.orderInfo = data;
      return data;
    },

    submitCart(amazonCheckoutSessionId: string | null = null, threeDsAuthId: string | null = null) {
      useGlobalStore().resetValidationBaseErrors();

      return useGraphql<PublicsOrdersResultType>(
        cartQueries.submitCart,
        {
          userAgent: window.navigator.userAgent,
          input: this.cartSubmitVariables,
          amazonCheckoutSessionId: amazonCheckoutSessionId,
          threeDsAuthId: threeDsAuthId,
        },
        { toasted: true },
      );
    },

    fetchOffer(orderNumber: string) {
      return useGraphql<OfferType>(cartQueries.ordersOfferFetch, { orderNumber });
    },

    async validateOfferCvUpsell(
      orderNumber: string,
      variantId: number | null,
      subsOrderData: SubsOrderDataInput,
      specifyDeliveryDate: SpecifyDeliveryDateInput,
      shippingCarrierId: number,
      skipCvConfirm: boolean,
      offerPosition: number | null,
    ) {
      useGlobalStore().resetValidationBaseErrors();

      const data = await useGraphql<PublicsOrdersPricesType>(
        cartQueries.ordersCvUpsellModificationValidate,
        {
          input: {
            orderNumber,
            variantId,
            subsOrderData,
            specifyDeliveryDate,
            shippingCarrierId,
            skipCvConfirm,
            offerPosition,
          },
        },
      );

      // TODO AZM
      this.orderNumberCvConfirm = orderNumber;
      this.currentMode = UPSELL_MODE;
      return data;
    },

    async validateOfferCvXsell(
      orderNumber: string,
      variantData: VariantSelectedInput[],
      subsOrderData: SubsOrderDataInput,
      specifyDeliveryDate: SpecifyDeliveryDateInput,
      shippingCarrierId: number,
      skipCvConfirm: boolean,
      offerPosition: number | null,
    ) {
      useGlobalStore().resetValidationBaseErrors();

      const data = await useGraphql<PublicsOrdersPricesType>(
        cartQueries.ordersCvXsellModificationValidate,
        {
          input: {
            orderNumber,
            variantData,
            subsOrderData,
            specifyDeliveryDate,
            shippingCarrierId,
            skipCvConfirm,
            offerPosition,
          },
        },
      );

      // TODO AZM
      this.orderNumberCvConfirm = orderNumber;
      this.currentMode = XSELL_MODE;
      return data;
    },

    submitOfferCvUpsell(
      orderNumber: string,
      variantId: number | null,
      subsOrderData: SubsOrderDataInput,
      specifyDeliveryDate: SpecifyDeliveryDateInput,
      shippingCarrierId: number,
      amazonCheckoutSessionId: string | null,
      paygentThreeDsAuthId: string | null,
      offerPosition: number | null,
    ) {
      useGlobalStore().resetValidationBaseErrors();

      return useGraphql(cartQueries.ordersCvUpsellModify, {
        input: {
          orderNumber,
          variantId,
          subsOrderData,
          specifyDeliveryDate,
          shippingCarrierId,
          amazonCheckoutSessionId,
          paygentThreeDsAuthId,
          offerPosition,
        },
      });
    },
    submitOfferCvXsell(
      orderNumber: string,
      variantData: VariantSelectedInput[],
      subsOrderData: SubsOrderDataInput,
      specifyDeliveryDate: SpecifyDeliveryDateInput,
      shippingCarrierId: number,
      amazonCheckoutSessionId: string | null,
      paygentThreeDsAuthId: string | null,
      offerPosition: number | null,
    ) {
      useGlobalStore().resetValidationBaseErrors();
      return useGraphql(cartQueries.ordersCvXsellModify, {
        input: {
          orderNumber,
          variantData,
          subsOrderData,
          specifyDeliveryDate,
          shippingCarrierId,
          amazonCheckoutSessionId,
          paygentThreeDsAuthId,
          offerPosition,
        },
      });
    },
    setPaymentMethodCode(code: string) {
      this.cart.paymentData.paymentMethodCode = code;
    },

    async calculateSubsOrderEstimate(input: any) {
      const data = await useGraphql<any>(cartQueries.calculateSubsOrderEstimate, {
        input: input,
      });

      return data?.estimatedTime;
    },

    ordersPaymentAmountValidate(
      amount: String,
      paymentMethodCode: String,
      isRecurring: Boolean | any,
    ) {
      return useGraphql<any>(commonQueries.ordersPaymentAmountValidate, {
        amount,
        paymentMethodCode,
        isRecurring,
      });
    },

    setCartProps(payload: any) {
      this.cartProps = payload;
    },

    // error: the rejection useGraphql produced. It is the only reliable signal
    // for "the server never answered" - the stores cannot be read for that,
    // because almost every real server failure lands under validationErrors
    // without ever touching validationBaseErrors.
    handleErrorWhenSubmit(error?: unknown) {
      const globalStore = useGlobalStore();

      if (!globalStore.validationErrors) return;

      if (this.currentMode == UPSELL_MODE) {
        this.cart.productsData = this.preventiveProductsData;
        this.cart.subsOrderData = this.preventiveSubsOrderData;
        this.cart.specifyDeliveryDate = this.preventiveSpecifyDeliveryDate;
        this.cart.urlData.productUpsellId = null;
        this.cart.shippingCarrierId = this.preventiveShippingCarrierId;
      }

      this.cart.urlData.upsellNormal = false;

      globalStore.keepError = true;
      globalStore.keepBaseError = true;

      // The navigation below destroys the toast useGraphql raised, so carry a
      // message across it - but ONLY when the server said nothing at all.
      //
      // An earlier version keyed this on validationBaseErrors being empty,
      // which is not that test: getErrors("base_errors") reads a TOP-LEVEL
      // key, and a declined card fails with { base: { base: [...] } }, so it
      // was empty for almost every real server failure. The customer then got
      // their decline reason AND "a communication error occurred, try again
      // later" - told to wait when they needed to fix their card.
      if (error instanceof Error && isServerSilent(error.message)) {
        globalStore.validationBaseErrors = [TRANSPORT_ERROR_MESSAGE];
      }

      // Read before $reset(): currentBaseUrl is cart state, so resetting
      // blanks it and the redirect becomes "/lp?u=", which UrlsController
      // answers with the 404 page.
      const baseUrl = this.currentBaseUrl;

      if (globalStore.resetData) this.$reset();

      window.location.href = "/lp?u=" + baseUrl;
    },

    async calculatePriceOrder(input: any) {
      const data = await useGraphql<PublicsOrdersPricesType>(
        cartQueries.publicsOrdersCalculatePrice,
        {
          input: input,
        },
      );
      this.orderSummary = data;
      return data;
    },
  },

  getters: {
    cartSubmitVariables() {
      const omission = CART_UNSUBMITTED_ATTRIBUTES;

      if (useAuthStore().isLoggedIn) {
        omission.push(...CUSTOMER_NONLOGIN_ATTRIBUTES);
      }

      if (this.cart.paymentData.paymentMethodCode != "sbps_creditcard_one_time") {
        this.cart.paymentData = _.omit(this.cart.paymentData, "cardTokenKey") as any;
      }

      var cartSubmit = _.cloneDeep(this.cart);

      cartSubmit.productsData = _.map(cartSubmit.productsData, function (product) {
        return _.omit(product, "isRecurring");
      });

      return _.omit(_.pick(cartSubmit, CART_SUBMITTED_ATTRIBUTES), omission);
    },

    resetRequired:
      (state) =>
      (baseUrl = "") => {
        const globalStore = useGlobalStore();
        const keepError = globalStore.keepError;

        if (state.currentBaseUrl != baseUrl) return true;
        else if (keepError || state.lastPage == "confirm") return false;

        return true;
      },
  },

  persist: {
    key: "united-cart-local",
    storage: window.localStorage,
    paths: [
      "cart",
      "orderInfo",
      "orderSummary",
      "shippingAddresses",
      "urlInfo",
      "offerData",
      "shippingCarrierDefault",
      "deliveryTimesOptions",
      "deliveryDateAtOptions",
      "preventiveProductsData",
      "preventiveSubsOrderData",
      "preventiveSpecifyDeliveryDate",
      "preventiveShippingCarrierId",
      "currentMode",
      "currentBaseUrl",
      "lastPage",
      "amazonBuyerData",
      "orderNumberCvConfirm",
    ],
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
